import { calculateAircraftImageDimensions } from './../../../../screens/Hangar/HangarLayout/DraggableAircraftNew/Scale'
import { 
  addCoords, 
  scaleCoord,
  subtractCoords,
  Coordinate,
} from './../../../../utils/coordinates'
import { 
  convertDegreesToRadians,
} from './../../../../utils/math'
import {
  rotateAroundOrigin,
} from './../../../../utils/coordinates'
import { Aircraft } from './../../../../types'
import { memorize } from './../../../../utils/optimization'

// Local type definition (./parking module doesn't exist)
type ParkingPosition = {
  aircraft: Aircraft;
  aircraftPosition: Coordinate;
  aircraftAngle: number;
};

/**
 * Returns what position you need to set the aircraft too when you want to
 * anchor it to the nose, tail or center to a specific point and at a specific
 * rotation.
 */
export const anchorAircraftToPoint = function(
  params: {
    aircraft: Aircraft,
    anchorPosition: Coordinate,
    aircraftClockAngle: number,
    feetToPixels?: number,
    anchorOn: "nose" | "tail" | "center" | "true_nose" | "true_tail",
  }
): Coordinate {
  const {
    aircraft,
    anchorPosition,
    aircraftClockAngle,
    feetToPixels = 1,
    anchorOn = 'nose',
  } = params


  var keyPositions = getAircraftKeyPositions(
    {
      aircraft,
      aircraftOriginPosition: anchorPosition, 
      aircraftClockAngle,
    },
    feetToPixels
  )

  // Now check how the rotation moved the point we care about away from
  // our target anchor point and we'll return that + our target anchor point
  switch (anchorOn){
    case 'nose':
      var translation = subtractCoords(anchorPosition, keyPositions.nose)
      break;
    case 'true_nose':
      var translation = subtractCoords(anchorPosition, keyPositions.top) 
      break;
    case 'tail':
      var translation = subtractCoords(anchorPosition, keyPositions.tail)
      break;
    case 'true_tail':
      // Note we don't use the tail position as the tips of the tail wings
      // can overlap the line, so instead we use the bottom most position
      // of the aircraft
      var translation = subtractCoords(anchorPosition, keyPositions.bottom)
      break;
    case 'center':
      var translation = subtractCoords(anchorPosition, keyPositions.center)
      break;
    default:
      throw new Error(`Unknown anchorOn ${anchorOn}.`)
  }    
  
  return addCoords(anchorPosition, translation)
}




/**
 * Given an aircraft position, let's get a box that represents the area
 * it takes up with parking.
 * Note: This isn't in use but leaving it here encase it's useful to use
 * a simpler polygon/box of the aircraft instead of a more accurate one, e.g.
 * could make the algorithm faster or be used to ensure wings between small
 * and large aircraft won't collide (but without tight parking)
 */
export const convertParkingPositionIntoAircraftBox = function(
  params: {
    parkingPosition: ParkingPosition,
    feetToPixels: number,
  }
) : Array<Coordinate> {

  const {
    parkingPosition,
    feetToPixels,
  } = params

  // Create a box on the area that the plane takes up. This box is going to
  // be different based on how the plane is anchored to the line.
  // Let's start with nose to keep it simple
  // Multiplying it by feetToPixels here gives it the true value in pixel
  // space
  var wingspanPx = feetToPixels * parkingPosition.aircraft.wingspan
  var heightPx = feetToPixels * parkingPosition.aircraft.length

  // Let's draw a line from the nose to the tail
  var nosePos = getAircraftNosePosition({
    ...parkingPosition,
    // We don't want to rotate yet because we need the wing to align along
    // the x axis for easy math
    aircraftAngle: 0,
  }, feetToPixels)
  var tailPos = {
    x: nosePos.x,
    y: nosePos.y - heightPx, 
  }
  var centerPos = getAircraftCenterPosition({
    ...parkingPosition,
    aircraftAngle: 0,
  }, feetToPixels)
  var leftWingPos = {
    x: centerPos.x - (wingspanPx / 2),
    y: centerPos.y,
  }
  var rightWingPos = {
    x: centerPos.x + (wingspanPx / 2),
    y: centerPos.y,
  }
  // TODO We could convert our cross into a box here
  var box = [
    { x: leftWingPos.x, y: nosePos.y },
    { x: rightWingPos.x, y: nosePos.y },
    { x: rightWingPos.x, y: tailPos.y },
    { x: leftWingPos.x, y: tailPos.y },
  ]

  // Let's now rotate our cross to where it should be along the line
  var rearWheelPos = getAircraftRearWheelsPosition(parkingPosition, feetToPixels)
  
  // Rotate our box points
  box = box.map(point => rotateAroundOrigin(
    point,
    convertDegreesToRadians(parkingPosition.aircraftAngle),
    rearWheelPos
  ))

  return box
}

export const convertParkingPositionIntoAircraftPolygon = function(
  params: {
    parkingPosition: ParkingPosition,
    feetToPixels: number,
  }
) : Array<Coordinate> {

  const {
    parkingPosition,
    feetToPixels,
  } = params

  return getAircraftKeyPositions(
    {
      aircraft: parkingPosition.aircraft, 
      aircraftOriginPosition: parkingPosition.aircraftPosition, 
      aircraftClockAngle: parkingPosition.aircraftAngle,
    }, 
    feetToPixels
  ).aircraftGeom
}

/**
 * Get aircraft nose position in coordinate space.
 */
export const getAircraftNosePosition = function(
  params: {
    aircraft: Aircraft,
    aircraftPosition: Coordinate,
    /** 
    * Rotation of aircraft in degrees about the z axis, in the y plane.
    */
    aircraftAngle: number,
  }, 
  feetToPixels: number
) : Coordinate {

  var { aircraft, aircraftPosition, aircraftAngle } = params

  return getAircraftKeyPositions(
    {
      aircraft, 
      aircraftOriginPosition: aircraftPosition, 
      aircraftClockAngle: aircraftAngle,
    },
    feetToPixels
  ).nose
}

/**
 * Get aircraft tail position in coordinate space.
 */
export const getAircraftTailPosition = function(
  params: {
    aircraft: Aircraft,
    aircraftPosition: Coordinate,
    /** 
    * Rotation of aircraft in degrees about the z axis, in the y plane.
    */
    aircraftAngle: number,
  }, 
  feetToPixels: number
): Coordinate {
  var { aircraft, aircraftPosition, aircraftAngle } = params
  return getAircraftKeyPositions(
    {
      aircraft, 
      aircraftOriginPosition: aircraftPosition, 
      aircraftClockAngle: aircraftAngle,
    },
    feetToPixels
  ).tail

}

/**
 * Get the aircraft center position in coordinate space.
 */
export const getAircraftCenterPosition = function(
  params: {
    aircraft: Aircraft,
    aircraftPosition: Coordinate,
    /** 
    * Rotation of aircraft in degrees about the z axis, in the y plane.
    */
    aircraftAngle: number,
  }, 
  feetToPixels: number,
) : Coordinate {

  var { 
    aircraft, 
    aircraftPosition, 
    aircraftAngle, 
  } = params

  return getAircraftKeyPositions(
    {
      aircraft, 
      aircraftOriginPosition: aircraftPosition, 
      aircraftClockAngle: aircraftAngle,
    },
    feetToPixels
  ).center
}

/**
 * Get the aircraft wheel position in coordinate space.
 */
export const getAircraftRearWheelsPosition = function(
  params: {
    aircraft: Aircraft,
    aircraftPosition: Coordinate,
    /** 
    * Rotation of aircraft in degrees about the z axis, in the y plane.
    */
    aircraftAngle: number,
  }, 
  feetToPixels: number
) : Coordinate {
  var { 
    aircraft, 
    aircraftPosition, 
    aircraftAngle, 
  } = params
  return getAircraftKeyPositions(
    {
      aircraft, 
      aircraftOriginPosition: aircraftPosition, 
      aircraftClockAngle: aircraftAngle,
    },
    feetToPixels
  ).rearWheels
}

type GetAircraftKeyPositionsResult = {
  nose: Coordinate,
  top: Coordinate,
  rearWheels: Coordinate,
  tail: Coordinate,
  bottom: Coordinate,
  center: Coordinate,
  aircraftGeom: Coordinate[],
}

/**
 * Given an aircraft in coordinate space this will abstract away the 
 * internal relative aircraft coordinates into coordinates that are in 
 * the coordinate space.
 * e.g. an aircraft nose position is relative to a 600x600 pixel image, this
 * will return that nose position in coordinate space based on where the
 * aircraft is positioned and how that 600x600 image is mapped on to the
 * coordinate space.
 */
export const getAircraftKeyPositions = memorize(
  function(params, feetToPixels: number = 1): GetAircraftKeyPositionsResult{

    const { 
      aircraft, 
      aircraftOriginPosition, 
      aircraftClockAngle,
    } : {
      aircraft: Aircraft,
      aircraftOriginPosition: Coordinate,
      /** 
      * Rotation of aircraft in degrees about the z axis, in the y plane.
      */
      aircraftClockAngle: number,
    } = params

    // These are the key points relative to the 600x600 pixel aircraft image.
    var relativeNosePosition600x = { 
      x: 600 - aircraft.nose.coordinates[0],
      y: 600 - aircraft.nose.coordinates[1],
    }
    var relativeTopOfAircraftPosition600x = {
      x: aircraft.midline,
      y: Math.max(...aircraft.geom.coordinates[0].map(o => o[1])),
    }
    var relativeTailPosition600x = {
      x: aircraft.midline,
      y: aircraft.tail_bottom,
    }
    var relativeBottomOfAircraftPosition600x = {
      x: aircraft.midline,
      y: Math.min(...aircraft.geom.coordinates[0].map(o => o[1])),
    }
    var relativeRearWheelsPosition600x = {
      x: 600 - aircraft.midline,
      y: 600 - aircraft.rear_wheels,
    }
    var relativeCenterPosition600x = {
      x: 600 - aircraft.centroid.coordinates[0],
      y: 600 - aircraft.centroid.coordinates[1],
    }

    var aircraftGeomPolygon600x = aircraft.geom.coordinates[0]
    aircraftGeomPolygon600x = aircraftGeomPolygon600x.map(o => ({ 
      x: 600 - o[0], 
      y: 600 - o[1] 
    }))

    // This is the aircraft size in pixels.
    var aircraftSizePx = calculateAircraftImageDimensions(feetToPixels, aircraft)

    // These are the key points but relative to the pixel dimensions on screen.
    // e.g. if the image is rendered at 60x60 pixels then this will be the 
    // coordinates above scaled down to a 10th.
    var relativeNoseCoordPx = scaleCoord(relativeNosePosition600x, aircraftSizePx.height / 600)
    var relativeTopPosPx = scaleCoord(relativeTopOfAircraftPosition600x, aircraftSizePx.height / 600)
    var relativeTailPosPx = scaleCoord(relativeTailPosition600x, aircraftSizePx.height / 600)
    var relativeBottomPosPx = scaleCoord(relativeBottomOfAircraftPosition600x, aircraftSizePx.height / 600)
    var relativeRearWheelsPx = scaleCoord(relativeRearWheelsPosition600x, aircraftSizePx.height / 600)
    var relativeCenterPositionPx = scaleCoord(relativeCenterPosition600x, aircraftSizePx.height / 600)
    var relativeAircraftGeomPolygonPx = aircraftGeomPolygon600x.map(coord => scaleCoord(coord, aircraftSizePx.height / 600))
    
    // Set the relative positions against our target anchor position
    var noseCoordPx = addCoords(aircraftOriginPosition, relativeNoseCoordPx)
    var topPosPx = addCoords(aircraftOriginPosition, relativeTopPosPx)
    var rearWheelsPx = addCoords(aircraftOriginPosition, relativeRearWheelsPx)
    var tailPosPx = addCoords(aircraftOriginPosition, relativeTailPosPx)
    var bottomPosPx = addCoords(aircraftOriginPosition, relativeBottomPosPx)
    var centerPosPx = addCoords(aircraftOriginPosition, relativeCenterPositionPx)
    var aircraftGeomPolygonPx = relativeAircraftGeomPolygonPx.map(coord =>  addCoords(aircraftOriginPosition, coord))

    //drawDot(noseCoordPx, 'green')
    //drawDot(rearWheelsPx, 'orange')
    //drawDot(tailPosPx, 'yellow')
    //drawDot(centerPosPx, 'blue')

    // Do our rotation around the rear wheels
    noseCoordPx = rotateAroundOrigin(
      noseCoordPx,
      convertDegreesToRadians(aircraftClockAngle),
      rearWheelsPx
    )
    topPosPx = rotateAroundOrigin(
      topPosPx,
      convertDegreesToRadians(aircraftClockAngle),
      rearWheelsPx
    )
    // Probably don't need to do this rotation but leaving it in so it's 
    // consistent with the method for all points
    rearWheelsPx = rotateAroundOrigin(
      rearWheelsPx,
      convertDegreesToRadians(aircraftClockAngle),
      rearWheelsPx
    )
    tailPosPx = rotateAroundOrigin(
      tailPosPx,
      convertDegreesToRadians(aircraftClockAngle),
      rearWheelsPx
    )
    bottomPosPx = rotateAroundOrigin(
      bottomPosPx,
      convertDegreesToRadians(aircraftClockAngle),
      rearWheelsPx
    )
    centerPosPx = rotateAroundOrigin(
      centerPosPx,
      convertDegreesToRadians(aircraftClockAngle),
      rearWheelsPx
    )

    aircraftGeomPolygonPx = aircraftGeomPolygonPx.map(coord => rotateAroundOrigin(
      coord,
      convertDegreesToRadians(aircraftClockAngle),
      rearWheelsPx
    ))
    return {
      nose: noseCoordPx,
      top: topPosPx,
      rearWheels: rearWheelsPx,
      tail: tailPosPx,
      bottom: bottomPosPx,
      center: centerPosPx,
      aircraftGeom: aircraftGeomPolygonPx,
    }
  },
  {
    cacheKey: (params, feetToPixels) => 'getAircraftKeyPositions:' + JSON.stringify(params) + ':' + String(feetToPixels),
  }
)

